import type {
  DictionaryStore,
  SearchMode,
  SearchResult,
  TokenizedWord,
  ResultFilter,
} from "./types";
import { tokenize, isChinese } from "./tokenizer";

export function detectSearchMode(query: string): SearchMode {
  // Check for wildcard patterns
  if (query.includes("*") || query.includes("?")) {
    return "wildcard";
  }

  // Check for Chinese characters
  for (const char of query) {
    if (isChinese(char)) {
      return "chinese";
    }
  }

  // All other text searches both English and Jyutping
  return "text";
}

function normalizeJyutping(jyutping: string): string {
  return jyutping.toLowerCase().replace(/\s+/g, " ").trim();
}

// Strip tone numbers for flexible matching
function stripTones(jyutping: string): string {
  return jyutping.toLowerCase().replace(/[1-6]/g, "").replace(/\s+/g, " ").trim();
}

function wildcardToRegex(pattern: string): RegExp {
  // Escape regex special chars except * and ?
  const escaped = pattern.replace(/[.+^${}()|[\]\\]/g, "\\$&");
  // Convert wildcards to regex
  const regexPattern = escaped.replace(/\*/g, ".*").replace(/\?/g, ".");
  return new RegExp(regexPattern, "i");
}

export function searchChinese(
  query: string,
  store: DictionaryStore
): { tokens: TokenizedWord[]; results: SearchResult[] } {
  const tokens = tokenize(query, store.trieRoot);
  const results: SearchResult[] = [];

  // Collect all entries from tokens
  for (const token of tokens) {
    for (const entry of token.entries) {
      results.push({
        entry,
        matchType: "traditional",
        matchedText: token.text,
      });
    }
  }

  // If no exact tokenization matches, try contains search
  if (results.length === 0) {
    for (const entry of store.entries) {
      if (entry.traditional.includes(query)) {
        results.push({
          entry,
          matchType: "traditional",
          matchedText: query,
        });
      }
    }
  }

  return { tokens, results };
}

export function searchJyutping(
  query: string,
  store: DictionaryStore
): SearchResult[] {
  const normalized = normalizeJyutping(query);
  const strippedQuery = stripTones(query);
  const results: SearchResult[] = [];
  const seen = new Set<string>();

  // Exact match first (with tones)
  const exactMatches = store.jyutpingIndex.get(normalized);
  if (exactMatches) {
    for (const entry of exactMatches) {
      const key = entry.traditional + entry.jyutping;
      if (!seen.has(key)) {
        seen.add(key);
        results.push({
          entry,
          matchType: "jyutping",
          matchedText: query,
        });
      }
    }
  }

  // Contains match (with tone-stripped comparison)
  for (const entry of store.entries) {
    const key = entry.traditional + entry.jyutping;
    if (seen.has(key)) continue;

    const entryJyutpingStripped = stripTones(entry.jyutping);

    // Match if query (without tones) is contained in entry (without tones)
    if (entryJyutpingStripped.includes(strippedQuery)) {
      seen.add(key);
      results.push({
        entry,
        matchType: "jyutping",
        matchedText: query,
      });
    }
  }

  return results;
}

// Calculate relevance score for English definition matches
// Higher score = better match
function scoreEnglishMatch(query: string, definitions: string[]): number {
  const queryLower = query.toLowerCase();
  let bestScore = 0;

  for (const def of definitions) {
    const defLower = def.toLowerCase();

    if (!defLower.includes(queryLower)) continue;

    let score = 0;

    // Exact match of entire definition is best
    if (defLower === queryLower) {
      score = 10000;
    }
    // Definition starts with query
    else if (defLower.startsWith(queryLower)) {
      score = 5000;
    }
    // Query appears as a complete word (word boundary match)
    else {
      const wordBoundaryRegex = new RegExp(`\\b${queryLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      if (wordBoundaryRegex.test(def)) {
        score = 1000;
      } else {
        // Substring match
        score = 100;
      }
    }

    // Prefer shorter definitions (more specific matches)
    // Subtract a small penalty based on definition length
    score -= Math.min(def.length, 500) * 0.1;

    bestScore = Math.max(bestScore, score);
  }

  return bestScore;
}

export function searchEnglish(
  query: string,
  store: DictionaryStore
): SearchResult[] {
  const queryLower = query.toLowerCase();
  const results: { result: SearchResult; score: number }[] = [];

  for (const entry of store.entries) {
    for (const def of entry.definitions) {
      if (def.toLowerCase().includes(queryLower)) {
        const score = scoreEnglishMatch(query, entry.definitions);
        results.push({
          result: {
            entry,
            matchType: "english",
            matchedText: query,
          },
          score,
        });
        break; // Only add entry once
      }
    }
  }

  // Sort by score descending (best matches first)
  results.sort((a, b) => b.score - a.score);

  return results.map((r) => r.result);
}

export function searchWildcard(
  query: string,
  store: DictionaryStore
): SearchResult[] {
  const regex = wildcardToRegex(query);
  const results: SearchResult[] = [];
  const seen = new Set<string>();

  for (const entry of store.entries) {
    const key = entry.traditional + entry.jyutping;
    if (seen.has(key)) continue;

    // Search across all fields
    if (
      regex.test(entry.traditional) ||
      regex.test(entry.jyutping) ||
      entry.definitions.some((d) => regex.test(d))
    ) {
      seen.add(key);
      results.push({
        entry,
        matchType: "traditional",
        matchedText: query,
      });
    }
  }

  return results;
}

// Combined search across both English definitions and Jyutping
export function searchText(
  query: string,
  store: DictionaryStore
): SearchResult[] {
  const results: SearchResult[] = [];
  const seen = new Set<string>();

  // Search Jyutping
  const jyutpingResults = searchJyutping(query, store);
  for (const result of jyutpingResults) {
    const key = result.entry.traditional + result.entry.jyutping;
    if (!seen.has(key)) {
      seen.add(key);
      results.push(result);
    }
  }

  // Search English
  const englishResults = searchEnglish(query, store);
  for (const result of englishResults) {
    const key = result.entry.traditional + result.entry.jyutping;
    if (!seen.has(key)) {
      seen.add(key);
      results.push(result);
    }
  }

  return results;
}

export function filterResults(
  results: SearchResult[],
  filter: ResultFilter
): SearchResult[] {
  if (filter === "all") {
    return results;
  }
  return results.filter((r) => r.matchType === filter);
}

export function search(
  query: string,
  store: DictionaryStore
): { mode: SearchMode; tokens?: TokenizedWord[]; results: SearchResult[] } {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) {
    return { mode: "text", results: [] };
  }

  const mode = detectSearchMode(trimmedQuery);

  switch (mode) {
    case "chinese": {
      const { tokens, results } = searchChinese(trimmedQuery, store);
      return { mode, tokens, results };
    }
    case "text":
      return { mode, results: searchText(trimmedQuery, store) };
    case "wildcard":
      return { mode, results: searchWildcard(trimmedQuery, store) };
  }
}
